import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { ArrowRightLeft } from 'lucide-react';
import {
  accountsApi, chartApi, costCentersApi, partiesApi, transactionsApi,
  type Account, type ChartOfAccount, type CostCenter, type Party, type Transaction, type Direction,
  type TransactionsSummary,
} from '@/api';
import { MonthPeriodFilter } from '@/components/ui/MonthPeriodFilter';
import { SortHeader, makeToggleSort, cmp, type SortState } from '@/components/ui/sort-header';
import { currentMonthRange, formatBRL, type DateRange } from '@/lib/utils';
import { ApiError } from '@/lib/api';

const txSchema = z.object({
  direction: z.enum(['IN', 'OUT']),
  accountId: z.string().uuid(),
  chartOfAccountId: z.string().uuid(),
  costCenterId: z.string().uuid().optional().or(z.literal('')),
  partyId: z.string().uuid().optional().or(z.literal('')),
  amount: z.coerce.number().positive(),
  date: z.string(),
  description: z.string().min(1).max(500),
  reference: z.string().optional(),
  paymentMethod: z.enum(['PIX', 'BOLETO', 'TRANSFER', 'CARD', 'DEBIT', 'CASH', 'OTHER']),
});
type TxValues = z.infer<typeof txSchema>;

// Direction asc = entradas antes de saídas; desc inverte.
const directionOrder: Record<Direction, number> = { IN: 0, OUT: 1 };

// Status asc = pendentes primeiro (mais relevantes para checagem), depois confirmados, depois conciliados.
const txStatusOrder: Record<'PENDING' | 'CONFIRMED' | 'RECONCILED', number> = {
  PENDING: 0, CONFIRMED: 1, RECONCILED: 2,
};

type CaixaSortColumn =
  | 'date'
  | 'direction'
  | 'chart'
  | 'party'
  | 'description'
  | 'account'
  | 'amount'
  | 'status';

export function CaixaPage() {
  const qc = useQueryClient();
  const [openNew, setOpenNew] = useState(false);
  const [period, setPeriod] = useState<DateRange>(() => currentMonthRange());
  const [filters, setFilters] = useState<{ accountId?: string; direction?: Direction; q?: string; hideReversals?: boolean }>(
    { hideReversals: true },
  );
  const [sort, setSort] = useState<SortState<CaixaSortColumn>>({ column: 'date', dir: 'desc' });
  const toggleSort = makeToggleSort(setSort);

  const { data: accounts } = useQuery({ queryKey: ['accounts'], queryFn: accountsApi.list });
  const { data: txs, isLoading } = useQuery({
    queryKey: ['transactions', { ...filters, ...period }],
    queryFn: () => transactionsApi.list({ ...filters, from: period.from, to: period.to }),
  });

  const { data: summary } = useQuery({
    queryKey: ['transactions-summary', { ...filters, ...period }],
    queryFn: () => transactionsApi.summary({ ...filters, from: period.from, to: period.to }),
  });

  const reverseMut = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => transactionsApi.reverse(id, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transactions-summary'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  function handleReverse(t: Transaction) {
    const motivo = prompt(`Motivo do estorno de "${t.description}" (mín 10 caracteres):`);
    if (motivo && motivo.length >= 10) reverseMut.mutate({ id: t.id, motivo });
    else if (motivo !== null) alert('Motivo deve ter ao menos 10 caracteres');
  }

  // Linhas ordenadas pela coluna escolhida (filtro/paginação vêm do backend).
  const sortedTxs = useMemo(() => {
    const rows = txs?.items ?? [];
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let primary = 0;
      switch (sort.column) {
        case 'date':        primary = cmp.date(a.date, b.date); break;
        case 'direction':   primary = directionOrder[a.direction] - directionOrder[b.direction]; break;
        case 'chart':       primary = cmp.text(a.chartOfAccount.name, b.chartOfAccount.name); break;
        case 'party':       primary = cmp.text(a.party?.name ?? '', b.party?.name ?? ''); break;
        case 'description': primary = cmp.text(a.description, b.description); break;
        case 'account':     primary = cmp.text(a.account.name, b.account.name); break;
        case 'amount':      primary = cmp.number(a.amount, b.amount); break;
        case 'status':      primary = txStatusOrder[a.status] - txStatusOrder[b.status]; break;
      }
      if (primary !== 0) return primary * factor;
      // desempate estável: data desc + descrição
      const dateCmp = cmp.date(b.date, a.date);
      if (dateCmp !== 0) return dateCmp;
      return cmp.text(a.description, b.description);
    });
  }, [txs, sort]);

  return (
    <>
      <PageHeader
        title="Caixa"
        breadcrumb="Lançamentos"
        actions={
          <>
            {accounts && (
              <div className="text-sm bg-muted rounded-md px-3 py-1.5">
                Saldo: <span className="font-medium ml-1 text-emerald-700">
                  {formatBRL(accounts.reduce((s, a) => s + Number(a.balance), 0))}
                </span>
              </div>
            )}
            <Button size="sm" onClick={() => setOpenNew(true)}>+ Novo lançamento</Button>
          </>
        }
      />

      <PageBody>
        <MonthPeriodFilter value={period} onChange={setPeriod} />

        <CashSummaryCards summary={summary} />

        <div className="bg-card border rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs">Conta</Label>
            <Select value={filters.accountId ?? ''} onChange={(e) => setFilters((f) => ({ ...f, accountId: e.target.value || undefined }))}>
              <option value="">Todas</option>
              {accounts?.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Tipo</Label>
            <Select value={filters.direction ?? ''} onChange={(e) => setFilters((f) => ({ ...f, direction: (e.target.value || undefined) as Direction | undefined }))}>
              <option value="">Todos</option>
              <option value="IN">Entrada</option>
              <option value="OUT">Saída</option>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Buscar</Label>
            <Input placeholder="Descrição, contraparte..." value={filters.q ?? ''} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value || undefined }))} />
          </div>
          <label className="text-sm flex items-center gap-2 col-span-1 md:col-span-3">
            <input type="checkbox" checked={!!filters.hideReversals} onChange={(e) => setFilters((f) => ({ ...f, hideReversals: e.target.checked }))} />
            Ocultar estornados e originais
          </label>
        </div>

        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <SortHeader column="date"        label="Data"        sort={sort} onClick={toggleSort} />
                <SortHeader column="direction"   label="Tipo"        sort={sort} onClick={toggleSort} />
                <SortHeader column="chart"       label="Categoria"   sort={sort} onClick={toggleSort} />
                <SortHeader column="party"       label="Contraparte" sort={sort} onClick={toggleSort} />
                <SortHeader column="description" label="Descrição"   sort={sort} onClick={toggleSort} />
                <SortHeader column="account"     label="Conta"       sort={sort} onClick={toggleSort} />
                <SortHeader column="amount"      label="Valor"       sort={sort} onClick={toggleSort} align="right" />
                <SortHeader column="status"      label="Status"      sort={sort} onClick={toggleSort} />
                <th />
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading && <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Carregando…</td></tr>}
              {sortedTxs.map((t) => {
                const isTransfer = !!t.transferId;
                const isReversed = !!(t.reversedById || t.reversedBy.length > 0);
                return (
                  <tr key={t.id} className={isReversed ? 'opacity-50 line-through' : ''}>
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(t.date).toLocaleDateString('pt-BR')}</td>
                    <td className={`px-3 py-2 ${t.direction === 'IN' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {t.direction === 'IN' ? 'Entrada' : 'Saída'}
                    </td>
                    <td className="px-3 py-2">
                      {isTransfer ? (
                        <Badge variant="info" className="gap-1">
                          <ArrowRightLeft className="w-3 h-3" /> Transferência
                        </Badge>
                      ) : (
                        t.chartOfAccount.name
                      )}
                    </td>
                    <td className="px-3 py-2">{t.party?.name ?? '—'}</td>
                    <td className="px-3 py-2">{t.description}</td>
                    <td className="px-3 py-2">{t.account.name}</td>
                    <td className={`px-3 py-2 text-right font-medium ${t.direction === 'IN' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {t.direction === 'IN' ? '+' : '−'}{formatBRL(t.amount)}
                    </td>
                    <td className="px-3 py-2">
                      {isTransfer ? (
                        <span className="text-xs text-muted-foreground">Ajuste interno</span>
                      ) : (
                        <>
                          {t.status === 'CONFIRMED' && <Badge variant="success">Confirmado</Badge>}
                          {t.status === 'RECONCILED' && <Badge variant="info">Conciliado</Badge>}
                          {t.status === 'PENDING' && <Badge variant="warning">Pendente</Badge>}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isTransfer ? (
                        <span
                          className="text-xs text-muted-foreground"
                          title="Edite ou remova em Cadastros → Transferências"
                        >
                          —
                        </span>
                      ) : !isReversed ? (
                        <button onClick={() => handleReverse(t)} className="text-xs text-rose-600 hover:underline">Estornar</button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
              {!isLoading && (txs?.items.length ?? 0) === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">Nenhum lançamento</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {txs && summary && (
          <FilteredTotals summary={summary} pageCount={txs.items.length} totalCount={txs.total} />
        )}
      </PageBody>

      <NewTransactionDialog open={openNew} onClose={() => setOpenNew(false)} />
    </>
  );
}

function NewTransactionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit, watch, formState: { errors, isSubmitting }, reset } = useForm<TxValues>({
    resolver: zodResolver(txSchema),
    defaultValues: {
      direction: 'IN',
      date: new Date().toISOString().slice(0, 10),
      paymentMethod: 'PIX',
    },
  });

  const direction = watch('direction');
  const { data: accounts } = useQuery({ queryKey: ['accounts'], queryFn: accountsApi.list, enabled: open });
  const { data: charts } = useQuery({ queryKey: ['charts'], queryFn: chartApi.list, enabled: open });
  const { data: ccs } = useQuery({ queryKey: ['ccs'], queryFn: costCentersApi.list, enabled: open });
  const { data: parties } = useQuery({ queryKey: ['parties'], queryFn: () => partiesApi.list(), enabled: open });

  const filteredCharts = charts?.filter((c) =>
    direction === 'IN' ? c.nature === 'REVENUE' : c.nature === 'EXPENSE',
  );

  const create = useMutation({
    mutationFn: (v: TxValues) =>
      transactionsApi.create({
        ...v,
        amount: Number(v.amount),
        costCenterId: v.costCenterId || undefined,
        partyId: v.partyId || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['transactions-summary'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      reset();
      onClose();
    },
  });

  const apiError = create.error instanceof ApiError ? create.error.message : null;

  return (
    <Dialog open={open} onClose={onClose} title="Novo lançamento">
      <form onSubmit={handleSubmit((v) => create.mutate(v))}>
        <DialogBody className="space-y-3">
          <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-md">
            <label className={`px-3 py-2 text-center text-sm rounded cursor-pointer ${direction === 'IN' ? 'bg-card shadow-sm font-medium text-emerald-700' : 'text-muted-foreground'}`}>
              <input type="radio" value="IN" {...register('direction')} className="sr-only" />
              Entrada
            </label>
            <label className={`px-3 py-2 text-center text-sm rounded cursor-pointer ${direction === 'OUT' ? 'bg-card shadow-sm font-medium text-rose-700' : 'text-muted-foreground'}`}>
              <input type="radio" value="OUT" {...register('direction')} className="sr-only" />
              Saída
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Conta</Label>
              <Select {...register('accountId')}>
                <option value="">— selecione —</option>
                {accounts?.map((a: Account) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
              {errors.accountId && <p className="text-xs text-destructive">{errors.accountId.message}</p>}
            </div>
            <div className="space-y-1">
              <Label>Data</Label>
              <Input type="date" {...register('date')} />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Valor</Label>
            <Input type="number" step="0.01" placeholder="0,00" {...register('amount')} />
            {errors.amount && <p className="text-xs text-destructive">Valor inválido</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Categoria</Label>
              <Select {...register('chartOfAccountId')}>
                <option value="">— selecione —</option>
                {filteredCharts?.map((c: ChartOfAccount) => (
                  <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                ))}
              </Select>
              {errors.chartOfAccountId && <p className="text-xs text-destructive">{errors.chartOfAccountId.message}</p>}
            </div>
            <div className="space-y-1">
              <Label>Centro de custo</Label>
              <Select {...register('costCenterId')}>
                <option value="">—</option>
                {ccs?.map((cc: CostCenter) => <option key={cc.id} value={cc.id}>{cc.name}</option>)}
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Contraparte</Label>
            <Select {...register('partyId')}>
              <option value="">—</option>
              {parties?.map((p: Party) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Forma de pagamento</Label>
              <Select {...register('paymentMethod')}>
                <option value="PIX">PIX</option>
                <option value="BOLETO">Boleto</option>
                <option value="TRANSFER">Transferência</option>
                <option value="CARD">Cartão</option>
                <option value="DEBIT">Débito</option>
                <option value="CASH">Dinheiro</option>
                <option value="OTHER">Outro</option>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Documento (opcional)</Label>
              <Input placeholder="NF, recibo..." {...register('reference')} />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Descrição</Label>
            <Input placeholder="Ex: Mensalidade VPS — maio/2026" {...register('description')} />
            {errors.description && <p className="text-xs text-destructive">{errors.description.message}</p>}
          </div>

          {apiError && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">{apiError}</div>}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={isSubmitting || create.isPending}>
            {isSubmitting || create.isPending ? 'Salvando…' : 'Salvar lançamento'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}


// =====================================================================
// Cards de resumo do período (Pagos / Recebidos / Saldo do mês / Saldo final previsto)
// =====================================================================
interface CashSummaryProps { summary?: TransactionsSummary; }

function CashSummaryCards({ summary }: CashSummaryProps) {
  const paid = summary?.period.paid.amount ?? '0';
  const received = summary?.period.received.amount ?? '0';
  const monthBalance = summary?.period.monthBalance ?? '0';
  const monthBalanceNum = Number(monthBalance);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <CashCard
        label="Valores Pagos"
        amount={paid}
        subtitle={`${summary?.period.paid.count ?? 0} saídas no período`}
        tone="danger"
      />
      <CashCard
        label="Recebidos"
        amount={received}
        subtitle={`${summary?.period.received.count ?? 0} entradas no período`}
        tone="success"
      />
      <CashCard
        label="Saldo do Mês"
        amount={monthBalance}
        subtitle="Recebidos − Pagos"
        tone={monthBalanceNum >= 0 ? 'success' : 'danger'}
      />
    </div>
  );
}

type CashTone = 'danger' | 'success';

const CASH_TONES: Record<CashTone, { header: string; body: string; value: string }> = {
  danger:  { header: 'bg-rose-600 text-white',    body: 'bg-card border-rose-200',    value: 'text-rose-700' },
  success: { header: 'bg-emerald-600 text-white', body: 'bg-card border-emerald-200', value: 'text-emerald-700' },
};

function CashCard({ label, amount, subtitle, tone }: { label: string; amount: string; subtitle: string; tone: CashTone }) {
  const t = CASH_TONES[tone];
  return (
    <div className={`rounded-lg border overflow-hidden ${t.body}`}>
      <div className={`px-3 py-1.5 text-sm font-medium text-center ${t.header}`}>{label}</div>
      <div className="px-3 py-3 text-center">
        <div className={`text-xl font-medium ${t.value}`}>{formatBRL(amount)}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{subtitle}</div>
      </div>
    </div>
  );
}

// =====================================================================
// Totais do filtro (rodapé da tabela)
// =====================================================================
function FilteredTotals({
  summary, pageCount, totalCount,
}: { summary: TransactionsSummary; pageCount: number; totalCount: number }) {
  const f = summary.filtered;
  const netNum = Number(f.netAmount);
  return (
    <div className="bg-card border rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Lançamentos</div>
        <div className="font-medium mt-0.5">
          {f.count}
          <span className="text-muted-foreground font-normal"> ({f.inCount} entradas · {f.outCount} saídas)</span>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">Mostrando {pageCount} de {totalCount}</div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Total entradas</div>
        <div className="font-medium text-emerald-700 mt-0.5">{formatBRL(f.inAmount)}</div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Total saídas</div>
        <div className="font-medium text-rose-700 mt-0.5">{formatBRL(f.outAmount)}</div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Saldo (entradas − saídas)</div>
        <div className={`font-medium mt-0.5 ${netNum >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
          {formatBRL(f.netAmount)}
        </div>
      </div>
    </div>
  );
}
